import { createContext, useContext } from "react";

/*
 * Toast and confirm are imperative by nature — you call them from a mutation
 * callback, not from JSX. Two hooks over one provider:
 *
 *   const toast = useToast();
 *   toast("Rabbi Mahamud updated");
 *   toast("Could not save", "error");
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *       tone: "danger",
 *       title: "Delete this user?",
 *       body: <p>…</p>,
 *       okLabel: "Yes, delete user",
 *   });
 *   if (ok) doIt();
 *
 * confirm() resolving to a boolean rather than taking an onOk callback is
 * deliberate: the caller keeps its own control flow, so a mutation reads
 * top-to-bottom instead of being split across a callback.
 *
 * Context and hooks live apart from the provider component so react-refresh can
 * fast-refresh the provider — the same split as context/theme.js.
 */

export const FeedbackContext = createContext(null);

function useFeedback() {
    const ctx = useContext(FeedbackContext);
    if (!ctx) throw new Error("useToast/useConfirm must be used inside <FeedbackProvider>");
    return ctx;
}

/** toast(message, tone) — tone: "success" | "error" | "warning" | "info" */
export function useToast() {
    return useFeedback().toast;
}

/** confirm(options) → Promise<boolean> */
export function useConfirm() {
    return useFeedback().confirm;
}

export const TOAST_TONES = {
    success: {
        icon: '<path d="M20 6 9 17l-5-5"/>',
        color: "text-emerald-400",
        strokeWidth: 2.4,
    },
    error: {
        icon: '<circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16h.01"/>',
        color: "text-rose-400",
        strokeWidth: 2.2,
    },
    warning: {
        icon: '<path d="M10.3 4 2.6 17.5A1.6 1.6 0 0 0 4 20h16a1.6 1.6 0 0 0 1.4-2.5L13.7 4a1.6 1.6 0 0 0-2.8 0Z"/><path d="M12 9.5v4M12 17h.01"/>',
        color: "text-amber-400",
        strokeWidth: 2.2,
    },
    info: {
        icon: '<circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/>',
        color: "text-brand-300",
        strokeWidth: 2.2,
    },
};

export const CONFIRM_TONES = {
    danger: { tone: "rose", okVariant: "danger" },
    warning: { tone: "amber", okVariant: "danger" },
    success: { tone: "emerald", okVariant: "primary" },
    info: { tone: "brand", okVariant: "primary" },
};
